import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Close';
import ContactHelpTip from './ContactHelpTip';
import CONTACT_HELP from './contactHelpText';
import AdmicityAddressLookup from '../../../../../shared-components/AdmicityAddressLookup';
import {closeUnlessEscape, LabelsOnBorder, MENU_UNDER_FIELD} from './contactBoxRules';
import {useGetPeopleAtAddressQuery} from '../../../../../api/services/profilesService';
import {
    ADDRESS_RULE,
    ADDRESS_UPDATE_CHOICES,
    linkAddressLine,
    personAtAddressLine,
    COURT_ORDER_CHOICES,
    courtOrderYesLine,
    DECLARATION,
    canGoOn,
    contactFromExisting,
    contactPayload,
    contactSteps,
    courtOrderLine,
    defaultPriority,
    emailChangeLines,
    emptyNewContact,
    formatAddress,
    isAddressComplete,
    nameError,
    priorityError,
    priorityLine,
    questionFor,
    responsibilityNoHelp,
    responsibilityQuestion,
    responsibilityYesLine,
    whereChoices,
    emailQuestion,
    phoneQuestion
} from './addContactRules';

// AD-2719 (Maria, 17 October 2026): adding or editing a contact asks one question at a time, in the same style as
// the Create Report box, so schools and parents understand each choice and the home address is never forgotten.
// The fields, rules and confirmation boxes are the same as before, and the same requests are sent.

// Two answers to a question, in the order given.
const Choices = ({value, onChange, testId, choices}) => (
    <RadioGroup
        value={value === null ? '' : (value ? 'yes' : 'no')}
        onChange={(e) => onChange(e.target.value === 'yes')}
        sx={{gap: 0.5, pl: 1}}
        data-testid={testId}
    >
        {choices.map(([choice, label, help]) => (
            <FormControlLabel
                key={choice}
                value={choice}
                control={<Radio size="small" sx={{py: 0.5}}/>}
                sx={CHOICE_SX}
                // an answer with a pop-up shows an information icon to hover over or tap
                label={help
                    ? <Box component="span" sx={{display: 'block', lineHeight: 1.4}}><ContactHelpTip label={label} text={help}/></Box>
                    : <Box component="span" sx={{display: 'block', lineHeight: 1.4}}>{label}</Box>}
            />
        ))}
    </RadioGroup>
);

Choices.propTypes = {
    value: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    testId: PropTypes.string,
    choices: PropTypes.array.isRequired
};

// The radio buttons of the Create Report box.
const CHOICE_SX = {alignItems: 'flex-start', m: 0, minHeight: 32, '& .MuiFormControlLabel-label': {pt: '5px'}};

const Note = ({children, testId}) => (
    <Typography variant="body2" sx={{mt: 1.5}} data-testid={testId}>{children}</Typography>
);

Note.propTypes = {
    children: PropTypes.node,
    testId: PropTypes.string
};

const AddContactBox = ({
                           open,
                           mode,
                           existingContact,
                           isParentOwnContact,
                           studentName,
                           childAddress,
                           titles,
                           relations,
                           isParent,
                           busy,
                           validateContactDetails,
                           otherChildren = [],
                           studentId,
                           onSave,
                           onClose
                       }) => {
    const [contact, setContact] = useState(emptyNewContact());
    const [step, setStep] = useState('who');
    const [touched, setTouched] = useState({});
    const [detailsError, setDetailsError] = useState('');
    // AD-2798: which box the message belongs under ('email' or 'phone'); empty for a message about the whole page
    const [detailsField, setDetailsField] = useState('');

    useEffect(() => {
        if (open) {
            const start = mode === 'edit' && existingContact ? contactFromExisting(existingContact, childAddress) : emptyNewContact();
            setContact(start);
            setStep('who');
            // When editing, the priority already set is kept.
            setTouched(mode === 'edit' ? {priority: true} : {});
            setDetailsError('');
        }
    }, [open]);

    // AD-2721: staff adding a contact at a separate address see who else at the school lives there
    const lookForPeople = open && mode === 'add' && !isParent && Boolean(studentId) && contact.sameAddress === false &&
        isAddressComplete(contact.address);
    const {data: peopleAtAddress = []} = useGetPeopleAtAddressQuery({
        studentId,
        houseNumber: contact.address?.houseNumber ?? '',
        houseName: contact.address?.houseName ?? '',
        apartment: contact.address?.apartment ?? '',
        postcode: contact.address?.postcode ?? '',
        udprn: contact.address?.udprn ?? '',
        uprn: contact.address?.uprn ?? ''
    }, {skip: !lookForPeople});
    const people = lookForPeople ? peopleAtAddress : [];
    const steps = contactSteps({mode, isParentOwnContact, sameAddress: contact.sameAddress, peopleAtAddress: people});
    const stepNumber = steps.indexOf(step) + 1;
    const set = (field, value) => setContact(prev => ({...prev, [field]: value}));
    const touch = field => setTouched(prev => ({...prev, [field]: true}));
    const where = whereChoices(studentName);
    // A school sees the child's address before choosing it. A parent sees it only where they can see it today.
    const childAddressText = formatAddress(childAddress);
    const canChooseSameAddress = isParent || Boolean(childAddressText);
    const emailChange = mode === 'edit'
        ? emailChangeLines({originalEmail: existingContact?.email, newEmail: contact.email, isParentOwnContact, contact, studentName, otherChildren})
        : [];

    const back = () => (stepNumber <= 1 ? onClose() : setStep(steps[stepNumber - 2]));
    const next = () => {
        if (step === 'contact') {
            // The email and phone number are checked with the same rules, and messages, as before.
            const result = validateContactDetails(contactPayload(contact, mode, childAddress));
            if (!result.isValid) {
                setDetailsError(result.message);
                setDetailsField(result.field ?? '');
                return;
            }
        }
        setDetailsError('');
        setDetailsField('');
        setStep(steps[stepNumber]);
    };

    const line = {
        responsibility: CONTACT_HELP.parentalResponsibility,
        courtOrder: courtOrderLine(studentName),
        priority: priorityLine(contact)
    }[step] ?? '';

    const yesNo = value => (value ? 'Yes' : 'No');
    const homeAddress = contact.sameAddress ? where.sameOnCheckPage : formatAddress(contact.address);
    const forename = contact.forename.trim();
    const checkRows = [
        ['Title', titles.find(t => t.id === contact.titleId)?.description ?? ''],
        ['Forename', contact.forename.trim()],
        ['Surname', contact.surname.trim()],
        ['Relation Type', contact.relationType],
        ...(isParentOwnContact
            ? []
            : [
                ['Parental Responsibility', yesNo(contact.parentalResponsibility)],
                ['Legal Order', yesNo(contact.legalOrder)],
                ['Priority', contact.priority]
            ]),
        ['Email', contact.email.trim()],
        ['Phone Number', contact.phoneNumber.trim()],
        ['Home address', homeAddress],
        ...(steps.includes('linkAddress')
            ? [['Linked address', people.map(personAtAddressLine).join('; ')]]
            : []),
        ...(mode === 'edit' && contact.updateAllLinkedAddresses !== null
            ? [['Address Update Options', ADDRESS_UPDATE_CHOICES[contact.updateAllLinkedAddresses ? 'all' : 'current'][0]]]
            : [])
    ];

    // A parent editing their own contact changes the title only on this page, as today.
    const nameFieldsLocked = mode === 'edit' && isParentOwnContact;

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={closeUnlessEscape(onClose)}
            data-testid="add-contact-dialog"
            // Maria, 18 October 2026: not as tall; the box stays one height as the pages change, and fits a small screen
            PaperProps={{sx: {borderRadius: 3, height: 'min(600px, calc(100% - 64px))'}}}
        >
            <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                        {/* Maria, 18 October 2026: the only difference between adding and editing */}
                        {mode === 'edit' ? 'Edit Contact Information' : 'Add New Contact'}
                    </Typography>
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <CancelIcon fontSize="small"/>
                    </IconButton>
                </Stack>
                <Typography variant="h5" sx={{fontWeight: 600}} data-testid="add-contact-question">
                    {questionFor(step, contact, studentName, mode)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{mt: 1.5}} data-testid="add-contact-progress">
                    {steps.map((stepName, index) => (
                        <Box key={stepName} sx={{flex: 1, height: 4, borderRadius: 2, backgroundColor: index < stepNumber ? '#186BBE' : '#e3e8ef'}}/>
                    ))}
                </Stack>
            </Box>

            {/* Maria, 18 October 2026: the first page's fields are centred in the box from top to bottom */}
            {/* AD-2797: less space above the address and check pages, so they fit without scrolling */}
            <DialogContent sx={{px: 4, pt: ['address', 'check'].includes(step) ? 1 : 3, pb: ['address', 'check'].includes(step) ? 1 : 3, display: 'flex', flexDirection: 'column', justifyContent: ['who', 'contact'].includes(step) ? 'center' : 'flex-start'}}>
                {/* Maria, 18 October 2026: darker, so it is easy to read */}
                {line && (
                    <Typography variant="body1" sx={{mb: 2.5, lineHeight: 1.6, color: '#2f4050'}} data-testid="add-contact-line">
                        {line}
                    </Typography>
                )}
                {step === 'who' && (
                    // Maria, 18 October 2026: bigger fields with a little more space between them
                    <Stack spacing={2.5} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
                        <TextField
                            size="medium"
                            select
                            label="Title"
                            SelectProps={{MenuProps: MENU_UNDER_FIELD}}
                            value={contact.titleId ?? ''}
                            onChange={(e) => set('titleId', e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {titles.map(t => <MenuItem key={t.id} value={t.id}>{t.description}</MenuItem>)}
                        </TextField>
                        {['forename', 'surname'].map(field => (
                            <TextField
                            size="medium"
                                key={field}
                                required
                                disabled={nameFieldsLocked}
                                label={field === 'forename' ? 'Forename' : 'Surname'}
                                value={contact[field]}
                                onChange={(e) => set(field, e.target.value)}
                                onBlur={() => touch(field)}
                                error={Boolean(touched[field] && nameError(contact[field]))}
                                helperText={touched[field] ? nameError(contact[field]) : ''}
                                inputProps={{'data-testid': `add-contact-${field}`}}
                            />
                        ))}
                        <TextField
                            size="medium"
                            select
                            required
                            disabled={nameFieldsLocked}
                            label="Relation Type"
                            value={contact.relationType}
                            onChange={(e) => set('relationType', e.target.value)}
                            SelectProps={{MenuProps: MENU_UNDER_FIELD, SelectDisplayProps: {'data-testid': 'add-contact-relation'}}}
                        >
                            {relations.map(r => <MenuItem key={r.description} value={r.description}>{r.description}</MenuItem>)}
                        </TextField>
                    </Stack>
                )}

                {step === 'responsibility' && (
                    <>
                        <Typography sx={{fontWeight: 600, mb: 1}} data-testid="add-contact-responsibility-question">
                            {responsibilityQuestion(contact, studentName)}
                        </Typography>
                        <Choices
                            value={contact.parentalResponsibility}
                            onChange={v => setContact(prev => ({
                                ...prev,
                                parentalResponsibility: v,
                                // the priority follows the answer until someone changes it
                                priority: touched.priority ? prev.priority : defaultPriority(v)
                            }))}
                            testId="add-contact-responsibility"
                            choices={[['yes', 'Yes'], ['no', 'No']]}
                        />
                        {contact.parentalResponsibility && <Note testId="add-contact-yes-line">{responsibilityYesLine(contact, studentName)}</Note>}
                        {contact.parentalResponsibility === false && <Note testId="add-contact-no-line">{responsibilityNoHelp(contact, studentName)}</Note>}
                        {isParent && <Note testId="add-contact-declaration">{DECLARATION}</Note>}
                    </>
                )}

                {step === 'courtOrder' && (
                    <>
                        <Choices
                            value={contact.legalOrder}
                            onChange={v => set('legalOrder', v)}
                            testId="add-contact-court-order"
                            choices={[['no', COURT_ORDER_CHOICES.no], ['yes', COURT_ORDER_CHOICES.yes]]}
                        />
                        {contact.legalOrder && <Note testId="add-contact-court-order-line">{courtOrderYesLine(contact)}</Note>}
                        {isParent && <Note testId="add-contact-declaration">{DECLARATION}</Note>}
                    </>
                )}

                {step === 'priority' && (
                    <TextField
                        required
                        fullWidth
                        label={<ContactHelpTip label="Priority" text={CONTACT_HELP.priority}/>}
                        value={contact.priority}
                        onChange={(e) => {
                            touch('priority');
                            set('priority', e.target.value);
                        }}
                        error={Boolean(priorityError(contact.priority))}
                        helperText={priorityError(contact.priority)}
                        inputProps={{'data-testid': 'add-contact-priority', inputMode: 'numeric'}}
                    />
                )}

                {step === 'contact' && (
                    // Maria, 18 October 2026: bigger fields, centred, as on the first page; the email address is required
                    <Stack spacing={2.5} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
                        {/* Maria, 18 October 2026: a question above each box, and no words in the boxes */}
                        <Box>
                            <Typography component="label" htmlFor="add-contact-email" sx={{display: 'block', fontWeight: 600, mb: 0.75}} data-testid="add-contact-email-question">
                                {emailQuestion(contact)} <Box component="span" sx={{color: 'error.main'}}>*</Box>
                            </Typography>
                            <TextField
                                size="medium"
                                fullWidth
                                required
                                id="add-contact-email"
                                type="email"
                                value={contact.email}
                                onChange={(e) => set('email', e.target.value)}
                                error={detailsField === 'email'}
                                helperText={detailsField === 'email' ? detailsError : ''}
                                FormHelperTextProps={{'data-testid': 'add-contact-email-error'}}
                                inputProps={{'data-testid': 'add-contact-email'}}
                            />
                        </Box>
                        <Box>
                            <Typography component="label" htmlFor="add-contact-phone" sx={{display: 'block', fontWeight: 600, mb: 0.75}} data-testid="add-contact-phone-question">
                                <ContactHelpTip label={phoneQuestion(contact)} text={CONTACT_HELP.phone}/>
                            </Typography>
                            <TextField
                                size="medium"
                                fullWidth
                                id="add-contact-phone"
                                value={contact.phoneNumber}
                                onChange={(e) => set('phoneNumber', e.target.value)}
                                error={detailsField === 'phone'}
                                helperText={detailsField === 'phone' ? detailsError : ''}
                                FormHelperTextProps={{'data-testid': 'add-contact-phone-error'}}
                                inputProps={{'data-testid': 'add-contact-phone'}}
                            />
                        </Box>
                        {/* Maria, 18 October 2026: whether the contact lives with the student, on this page, with more
                            space before the question */}
                        <Box sx={{pt: 2.5}}>
                            <Typography sx={{fontWeight: 600, mb: 0.5}} data-testid="add-contact-where-question">
                                {questionFor('where', contact, studentName, mode)}
                            </Typography>
                            <RadioGroup
                                value={contact.sameAddress === null ? '' : (contact.sameAddress ? 'same' : 'separate')}
                                onChange={(e) => set('sameAddress', e.target.value === 'same')}
                                sx={{gap: 0.5, pl: 1, flexDirection: 'row', columnGap: 3}}
                                data-testid="add-contact-where"
                            >
                                {canChooseSameAddress && <FormControlLabel value="same" control={<Radio size="small" sx={{py: 0.5}}/>} sx={CHOICE_SX} label={where.same}/>}
                                <FormControlLabel value="separate" control={<Radio size="small" sx={{py: 0.5}}/>} sx={CHOICE_SX} label={where.separate}/>
                            </RadioGroup>
                        </Box>
                        {emailChange.map(text => (
                            <Typography key={text} variant="body2" sx={{fontWeight: 600}} data-testid="add-contact-email-change">{text}</Typography>
                        ))}
                        {detailsError && !detailsField && <Typography variant="body2" color="error" data-testid="add-contact-error">{detailsError}</Typography>}
                    </Stack>
                )}

                {step === 'address' && (
                    // Maria, 18 October 2026: less space between the address fields
                    <Box sx={{
                        mt: 1,
                        '& .MuiGrid-container': {marginTop: '0 !important', width: 'calc(100% + 12px)', marginLeft: '-12px'},
                        '& .MuiGrid-container > .MuiGrid-item': {paddingTop: '8px !important', paddingLeft: '12px !important'},
                        '& .MuiTextField-root, & .MuiFormControl-root': {my: 0},
                        '& .MuiInputBase-input': {py: '8.5px'}
                    }}>
                        <LabelsOnBorder>
                            <AdmicityAddressLookup
                                onAddressSelect={(address) => set('address', address)}
                                initialValue={contact.address}
                                defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
                                withReferences={mode === 'add'}
                            />
                        </LabelsOnBorder>
                        {contact.address && !isAddressComplete(contact.address) && (
                            <Typography variant="body2" color="error" sx={{mt: 0.5}} data-testid="add-contact-address-rule">{ADDRESS_RULE}</Typography>
                        )}
                        {mode === 'edit' && (
                            <Box sx={{mt: 1.5}}>
                                <Typography variant="h6" sx={{mb: 0.25}}>Address Update Options</Typography>
                                <RadioGroup
                                    value={contact.updateAllLinkedAddresses === null ? '' : (contact.updateAllLinkedAddresses ? 'all' : 'current')}
                                    onChange={(e) => set('updateAllLinkedAddresses', e.target.value === 'all')}
                                    data-testid="add-contact-address-update"
                                >
                                    {['current', 'all'].map(choice => (
                                        <FormControlLabel
                                            key={choice}
                                            value={choice}
                                            control={<Radio sx={{py: 0.5}}/>}
                                            label={(
                                                <Box>
                                                    <Typography variant="body1">{ADDRESS_UPDATE_CHOICES[choice][0]}</Typography>
                                                    <Typography variant="body2" sx={{color: '#2f4050', lineHeight: 1.3}}>{ADDRESS_UPDATE_CHOICES[choice][1]}</Typography>
                                                </Box>
                                            )}
                                        />
                                    ))}
                                </RadioGroup>
                            </Box>
                        )}
                    </Box>
                )}

                {step === 'linkAddress' && (
                    <Stack spacing={2} sx={{mt: 1}} data-testid="add-contact-link-address">
                        <Box component="ul" sx={{m: 0, pl: 3}}>
                            {people.map(person => (
                                <Typography component="li" variant="body1" key={person.simsPersonId}>
                                    {personAtAddressLine(person)}
                                </Typography>
                            ))}
                        </Box>
                        <Typography variant="body1" data-testid="add-contact-link-line">{linkAddressLine(forename)}</Typography>
                    </Stack>
                )}

                {step === 'check' && (
                    <Table size="small" data-testid="add-contact-check">
                        <TableBody>
                            {checkRows.map(([label, value]) => (
                                <TableRow key={label}>
                                    <TableCell sx={{fontWeight: 600, width: '40%', py: 0.75}}>{label}</TableCell>
                                    <TableCell sx={{py: 0.75}}>{value}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>

            <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between">
                    <Button data-testid="add-contact-back" variant="outlined" onClick={back}>Back</Button>
                    <Button
                        data-testid={step === 'check' ? 'add-contact-save' : 'add-contact-next'}
                        variant="contained"
                        onClick={step === 'check' ? () => onSave(contactPayload(contact, mode, childAddress, people)) : next}
                        disabled={busy || !canGoOn(step, contact)}
                        startIcon={busy ? <CircularProgress color="inherit" size={16}/> : undefined}
                        sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                    >
                        {step === 'check' ? 'Save' : 'Next'}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
};

AddContactBox.propTypes = {
    open: PropTypes.bool,
    mode: PropTypes.oneOf(['add', 'edit']),
    existingContact: PropTypes.object,
    isParentOwnContact: PropTypes.bool,
    // AD-2723: the parent's other children whose profiles also hold this email address
    otherChildren: PropTypes.arrayOf(PropTypes.string),
    studentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    studentName: PropTypes.string,
    childAddress: PropTypes.object,
    titles: PropTypes.array,
    relations: PropTypes.array,
    isParent: PropTypes.bool,
    busy: PropTypes.bool,
    validateContactDetails: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

AddContactBox.defaultProps = {
    mode: 'add',
    titles: [],
    relations: []
};

export default AddContactBox;
